/**
 * <hero-collection-slider-mobile>
 * Coverflow-style slider for the Hero Collection Slider section (mobile only).
 *
 * v12 changes:
 * - The previous fix (v11) synced the left/right <-> center transform-origin
 *   flip to the EASED scale instead of the raw position, which helped, but
 *   couldn't fully eliminate the pop: even flipping origin at scale 0.994
 *   instead of 1.0 still causes a real, non-zero positional shift (changing
 *   the anchor point while scale != 1 always moves the rendered box a
 *   little, no matter how close to 1 that scale is) -- just a smaller one.
 *   No deadzone size fixes that; it only shrinks the window where it's
 *   visible.
 * - This replaces the flip mechanism entirely. transform-origin is now
 *   ALWAYS 'center' (never touched by JS). The "keep the visible peek edge
 *   from retreating" effect (the reason the flip existed at all) is instead
 *   produced with a continuous translateX compensation, computed directly
 *   from the current eased scale every frame: translateX = sign * (1 -
 *   scale) * width / 2. That's a smooth function of scale with no branch or
 *   step in it anywhere -- as scale eases toward 1, translateX eases toward
 *   0 right along with it, so there's no instant left/right correction for
 *   the user to see, at any point in the motion.
 */
class HeroCollectionSliderMobile extends HTMLElement {
    connectedCallback() {
        this.track = this.querySelector('.hcs-track-m');
        this.slideEls = Array.from(this.querySelectorAll('.hcs-slide-m'));
        this.dashEls = Array.from(this.querySelectorAll('.hcs-dash-m span'));
        this.blurA = this.querySelector('.hcs-blur-a');
        this.pillEl = this.querySelector('.hcs-pill-m');
        this.captionLabelEl = this.querySelector('.hcs-caption-label-m');
        this.brandEl = this.querySelector('.hcs-brand-m');

        if (!this.track || this.slideEls.length === 0) return;

        const styles = getComputedStyle(this);
        this.scaleAmount = parseFloat(styles.getPropertyValue('--hcs-scale')) || 0.1;
        this.opacityAmount = parseFloat(styles.getPropertyValue('--hcs-opacity')) || 0.3;

        this.lastActiveIndex = -1;
        // Per-slide eased state, so each photo smooths independently.
        this.appliedScale = this.slideEls.map(() => 1);
        this.appliedOpacity = this.slideEls.map(() => 1);
        this.rafId = null;

        // Content (text/blur) changes are still driven by discrete index
        // changes, not every frame -- that part was never the wobble source.
        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.slideEls.forEach((el) => {
            el.addEventListener('click', () => this.scrollToSlide(el));
        });
        this.dashEls.forEach((dot, i) => {
            dot.addEventListener('click', () => this.scrollToSlide(this.slideEls[i]));
        });

        if (window.ResizeObserver) {
            const ro = new ResizeObserver(() => this.centerInstant(this.slideEls[this.getClosestIndex()]));
            ro.observe(this.track);
        }

        requestAnimationFrame(() => requestAnimationFrame(() => this.init()));
    }

    disconnectedCallback() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    centerOffset(el) {
        const trackRect = this.track.getBoundingClientRect();
        const itemRect = el.getBoundingClientRect();
        const delta = itemRect.left - trackRect.left;
        return this.track.scrollLeft + delta - (this.track.clientWidth - el.clientWidth) / 2;
    }

    scrollToSlide(el) {
        this.track.scrollTo({ left: this.centerOffset(el), behavior: 'smooth' });
    }

    centerInstant(el) {
        if (!el) return;
        this.track.scrollLeft = this.centerOffset(el);
    }

    getClosestIndex() {
        const trackCenter = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
        let closest = 0, closestDist = Infinity;
        this.slideEls.forEach((el, i) => {
            const r = el.getBoundingClientRect();
            const elCenter = r.left + r.width / 2;
            const dist = Math.abs(trackCenter - elCenter);
            if (dist < closestDist) { closestDist = dist; closest = i; }
        });
        return closest;
    }

    /** Runs every animation frame: eases the coverflow scale/opacity toward
     *  the current target instead of snapping straight to it, damping out
     *  fast small back-and-forth motion from native scroll-snap overshoot. */
    tick() {
        const trackCenter = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
        const EASE = 0.28; // higher = snappier, lower = smoother/laggier

        let closest = 0, closestDist = Infinity;

        this.slideEls.forEach((el, i) => {
            const r = el.getBoundingClientRect();
            const elCenter = r.left + r.width / 2;
            const dist = Math.abs(trackCenter - elCenter);
            if (dist < closestDist) { closestDist = dist; closest = i; }
            const norm = Math.min(dist / (this.track.clientWidth / 2), 1);

            const targetScale = 1 - norm * this.scaleAmount;
            const targetOpacity = 1 - norm * this.opacityAmount;

            this.appliedScale[i] += (targetScale - this.appliedScale[i]) * EASE;
            this.appliedOpacity[i] += (targetOpacity - this.appliedOpacity[i]) * EASE;

            const photoEl = el.querySelector('.hcs-photo-m');
            if (photoEl) {
                // transform-origin is now ALWAYS 'center' (set once in CSS,
                // never touched by JS) -- no more left/right flip, no more
                // discrete jump for it to cause, at any scale.
                //
                // The old flip existed to keep the visible peek edge (the
                // side facing the track center) from retreating out of view
                // as the slide shrunk around a center origin. That's now
                // done with a continuous translateX compensation instead:
                // shrinking around 'center' pulls the near edge inward by
                // (1 - scale) * width / 2; translating by that same amount,
                // in the opposite direction, cancels exactly that and keeps
                // the near edge anchored -- but as a smooth function of the
                // (already eased) scale, not a step function. As the slide
                // becomes active (scale -> 1), the compensation -> 0 on its
                // own, with nothing that ever needs to switch or flip.
                const s = this.appliedScale[i];
                const sign = elCenter < trackCenter ? 1 : -1;
                const translateX = sign * (1 - s) * (r.width / 2);

                photoEl.style.transform = 'translateX(' + translateX.toFixed(2) + 'px) scale(' + s.toFixed(4) + ')';
                photoEl.style.opacity = this.appliedOpacity[i].toFixed(3);
            }
            el.classList.toggle('active', i === closest);
        });

        this.dashEls.forEach((d, i) => d.classList.toggle('active', i === closest));

        if (closest !== this.lastActiveIndex) {
            this.lastActiveIndex = closest;
            this.updateContent(closest);
        }

        this.rafId = requestAnimationFrame(() => this.tick());
    }

    /** Runs only when the active slide actually changes: fade the text + swap blur backdrop. */
    updateContent(closest) {
        const activeSlide = this.slideEls[closest];
        if (!activeSlide) return;
        const { badge, caption, brand, accent1, image } = activeSlide.dataset;

        const applyText = () => {
            if (this.pillEl) this.pillEl.textContent = badge || '';
            if (this.captionLabelEl) this.captionLabelEl.textContent = caption || '';
            if (this.brandEl) this.brandEl.textContent = brand || '';
        };

        // Gentle fade instead of any positional movement.
        if (this.captionLabelEl && this.brandEl) {
            this.captionLabelEl.style.opacity = '0';
            this.brandEl.style.opacity = '0';
            setTimeout(() => {
                applyText();
                this.captionLabelEl.style.opacity = '1';
                this.brandEl.style.opacity = '1';
            }, 120);
        } else {
            applyText();
        }

        // NOTE: use backgroundColor (not the "background" shorthand) for the
        // fallback — assigning style.background='' would also wipe out
        // backgroundImage, since they're longhands of the same shorthand.
        if (this.blurA) {
            if (image) {
                this.blurA.style.backgroundImage = 'url(' + image + ')';
                this.blurA.style.backgroundColor = '';
            } else {
                this.blurA.style.backgroundImage = '';
                this.blurA.style.backgroundColor = accent1 || '#232323';
            }
        }
    }

    onScroll() {
        // The rAF loop (started in init()) does the actual per-frame work
        // continuously; the scroll event no longer needs to do anything
        // itself, but keeping the listener registered is harmless and
        // costs nothing since it's a no-op now.
    }

    init() {
        this.centerInstant(this.slideEls[0]);
        this.lastActiveIndex = -1; // force the first updateContent() call
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.tick();
    }
}

if (!customElements.get('hero-collection-slider-mobile')) {
    customElements.define('hero-collection-slider-mobile', HeroCollectionSliderMobile);
}

/**
 * Desktop dots positioning.
 * The dots are a single shared widget (Ella's banner-slider-component reads
 * ".dots-item" globally to toggle ".active" and count pages), so they can't
 * be duplicated per slide -- that would break its counting logic. But that
 * also means they can't just sit in normal document flow "under the brand
 * name" of whichever slide happens to be showing, since they live outside
 * the per-slide markup.
 *
 * v7: previously this guessed the "current" slide geometrically (closest
 * .hcs-left-top to the track's center). That's fragile -- it depends on
 * getBoundingClientRect() reflecting the finished scroll position at the
 * exact moment the 'scroll' event fires, which isn't guaranteed for
 * instant (non-smooth) scrollTo() jumps like the ones Ella's own dot/arrow
 * click handlers use. Navigating via dots could catch a stale position.
 *
 * Now it watches Ella's own ".dots-item.active" class directly via
 * MutationObserver -- that's the actual, authoritative signal for which
 * slide is current, set by Ella's code itself regardless of whether the
 * change came from a dot click, an arrow click, or a drag-scroll. No
 * geometry guessing needed at all.
 */
(function () {
    function initDotsPosition(root) {
        var dotsWrap = root.querySelector('.hcs-dots');
        var dotItems = Array.from(root.querySelectorAll('.dots-item'));
        var tops = Array.from(root.querySelectorAll('.hcs-left-top'));
        if (!dotsWrap || dotItems.length === 0 || tops.length === 0) return;

        function reposition() {
            var activeIndex = dotItems.findIndex(function (el) {
                return el.classList.contains('active');
            });
            var el = tops[activeIndex] || tops[0];
            var elRect = el.getBoundingClientRect();
            var rootRect = root.getBoundingClientRect();
            dotsWrap.style.top = (elRect.bottom - rootRect.top + 14) + 'px';
        }

        reposition();

        // The authoritative trigger: whenever Ella's code toggles which
        // .dots-item has the "active" class, recompute immediately.
        var observer = new MutationObserver(reposition);
        dotItems.forEach(function (item) {
            observer.observe(item, { attributes: true, attributeFilter: ['class'] });
        });

        // Still handle viewport/text-size changes (e.g. editor settings,
        // window resize) which don't touch the active class at all.
        window.addEventListener('resize', reposition);
        if (window.ResizeObserver) {
            tops.forEach(function (el) { new ResizeObserver(reposition).observe(el); });
        }
    }

    document.querySelectorAll('.hcs-banner-slider').forEach(initDotsPosition);
})();